import { useAuthContext } from 'contexts';
import type { Bundle } from 'fhir/r4';
import { FHIRProxyClient, FHIRUtils, FlagBuilder, ProvenanceBuilder, TaskBuilder } from 'fhir-sdoh';
import moment from 'moment';
import { useState } from 'react';
import type { InputOnChangeData } from 'semantic-ui-react';

import { BLANK_PAYLOAD, CODE_DISPLAYS } from './constants';
import type { AddTaskProps, TaskDetails } from './types';
import { getOrganizationReference } from './utils';

export const useAddTaskForm = (props: AddTaskProps) => {
  const { practitionerName, practitionerId, token } = useAuthContext();
  const {
    flatPractitioners,
    foodVendors: vendorList,
    handleAddTaskToggle,
    handleNewTask,
    setRenderURL,
  } = props;

  /*
   * Both are optional on `AddTaskProps` because the activity tabs, which are scoped
   * to neither one member nor one org, also mount the header. But `<AddTask>` itself
   * renders behind `{!isActivity && …}` in `../index`, which is exactly the branch
   * that supplies them — so a form that exists has both. Narrowed once here rather
   * than at the nine reads below, all of which build the Task/Provenance payload.
   */
  const orgCode = props.orgCode!;
  const patientId = props.patientId!;

  const [dimmerProcess, setDimmerProcess] = useState<boolean>(false);
  const [formCode, setFormCode] = useState<string | null>(null);
  const [formPriorirty, setFormPriority] = useState<string | null>(null);
  const [formStartDate, setFormStartDate] = useState<string | null>(null);
  const [formEndDate, setFormEndDate] = useState<string | null>(null);
  const [formDate, setFormDate] = useState<string | null>(null);
  const [formDescription, setFormDescription] = useState<string>('');
  const [formVendor, setFormVendor] = useState<string | null>(null);
  const [formOwner, setFormOwner] = useState<string | null>(null);
  const [recurringInterval, setRecurringInterval] = useState<number | null>(null);
  const [recurring, setRecurring] = useState<boolean>(false);
  const [referralId, setReferralId] = useState<string | null>(null);
  const [taskDetails, setTaskDetails] = useState<TaskDetails>(BLANK_PAYLOAD);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, fieldprops: InputOnChangeData) => {
    const newTaskDetails = { ...taskDetails };
    newTaskDetails.startDate = moment(new Date()).format('YYYY-MM-DD');
    /*
     * `InputOnChangeData` declares neither `name` nor a typed `value` — both arrive
     * through its index signature as `any`, which is what made every read and every
     * computed write below unsafe. The form gives each control a literal `name` and
     * a string `value`, so naming them once here types the whole branch chain.
     */
    const name = fieldprops['name'] as string;
    const value = fieldprops.value;
    if (name === 'endDate') {
      newTaskDetails.endDate = moment(value).format('YYYY-MM-DD');
      setFormEndDate(moment(value).format('YYYY-MM-DD'));
      setFormDate(value);
    } else if (name === 'code') {
      newTaskDetails.code = {
        code: value,
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `TASK_CATEGORIES` (what the
         * dropdown renders) carries `careplan` and `enrollment-action`, which
         * `CODE_DISPLAYS` has no entry for, so this is `undefined` for those two
         * while `TaskDetails['code'].display` declares `string`. The assertion
         * keeps `undefined` flowing where it already did; a `?? ''` would put an
         * empty display into the Task the form posts.
         */
        display: CODE_DISPLAYS[value] as string,
      };
      setFormCode(value);
    } else if (name === 'priority') {
      newTaskDetails.priority = value;
      setFormPriority(value);
    } else if (name === 'ownerRef') {
      newTaskDetails.ownerRef = {
        reference: 'Practitioner/' + value.toString(),
        display: flatPractitioners.filter((x) => x.id === value)[0]!.fullName,
      };
      setFormOwner(value);
    } else if (name === 'description') {
      newTaskDetails.description = value;
      setFormDescription(value);
    } else if (name === 'vendor') {
      newTaskDetails.restriction = {
        recipient: [
          {
            reference: 'Organization/' + value,
            display: vendorList.find((v) => v.value === value)?.text,
          },
        ],
      };
      setFormVendor(value);
    } else if (name === 'referralId') {
      newTaskDetails.referralId = value;
      setReferralId(value);
    }
    setTaskDetails({ ...taskDetails, ...newTaskDetails });
  };

  const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
  const fhirProxyClient = new FHIRProxyClient(token, proxyURL);

  const clearForm = () => {
    setFormPriority(null);
    setFormCode(null);
    setFormDate(null);
    setFormStartDate(null);
    setFormEndDate(null);
    setRecurringInterval(null);
    setRecurring(false);
    setFormDescription('');
    setFormOwner(null);
    setFormVendor(null);
    setTaskDetails(BLANK_PAYLOAD);
    setReferralId(null);
  };

  // Validation and submission logic
  const validateTaskDetails = async () => {
    const tempValidationErrors: string[] = [];

    if (!taskDetails?.code?.code) {
      tempValidationErrors.push('Please select task category');
    }

    if (!taskDetails.priority) {
      tempValidationErrors.push('Please select task priority');
    }

    if (props?.referralIdList?.length && !taskDetails.referralId) {
      tempValidationErrors.push('Please select referral ID');
    }

    if (!taskDetails?.ownerRef?.reference) {
      tempValidationErrors.push('Please select task owner');
    }

    if (recurring && (recurringInterval == null || recurringInterval < 1)) {
      tempValidationErrors.push('Interval (days) must be at least 1');
    }

    setValidationErrors(tempValidationErrors);

    if (tempValidationErrors.length === 0) {
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      recurring ? await submitTaskBundle() : await submitTask();
    }
  };

  const submitTask = async () => {
    setDimmerProcess(true);

    const orgRef = getOrganizationReference(orgCode);

    // if task code is outreach, default reason to "enrollment"
    if (taskDetails.code.code === 'outreach') {
      taskDetails.reason = 'enrollment';
    }

    let taskPayload = TaskBuilder.build(
      taskDetails.code,
      null,
      taskDetails.description,
      'Patient/' + patientId.toString(),
      ['Organization/' + orgCode],
      null,
      taskDetails.reason,
      taskDetails.note,
      null,
      taskDetails.startDate,
      taskDetails.endDate,
      taskDetails.ownerRef,
      practitionerName,
      taskDetails.restriction ? taskDetails.restriction.recipient : null,
      taskDetails.referralId,
      null,
      taskDetails.priority === 'asap' ? 'asap' : 'routine'
    );

    taskPayload.requester = {
      reference: 'Practitioner/' + practitionerId.toString(),
      display: practitionerName,
    };

    taskPayload = FHIRUtils.addUpdatedBy(taskPayload, practitionerName);

    const provenance = ProvenanceBuilder.build(
      `Task/${taskPayload.id}`,
      {
        reference: 'Practitioner/' + practitionerId,
        display: practitionerName,
      },
      patientId.toString(),
      'CREATE',
      `A ${taskPayload?.code?.coding?.[0]?.code} task has been created`,
      orgRef
    );
    let fhirBundle: Bundle;

    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. The test optional-chains all four
     * levels of the code path, so an absent code yields `undefined`, which
     * `includes` does not match, so `!includes(...)` is TRUE — a task with no code
     * enters the flag-building branch, where the same chain is dereferenced
     * unguarded and throws. The guard admits exactly the shape its body cannot
     * handle.
     *
     * `taskPayload.owner.reference` in the same call is a second absence:
     * `Task.owner` is `0..1` and its `reference` `0..1`, so an unassigned task
     * throws before the flag is posted. Both are reachable only through paths that
     * currently always set a code and an owner.
     */
    //do not create flag if a task is food delivery or consultation task
    if (!['consultation', 'food-delivery', 'other-goods-delivery'].includes(taskPayload?.code?.coding?.[0]?.code as string)) {
      const flagPayload = FlagBuilder.build(
        patientId.toString(),
        practitionerId,
        { code: 'admin', display: 'administrative' },
        { code: 'ALERT', display: 'Alert' },
        taskPayload.code!.coding![0]!.code!.toString().toUpperCase() +
          ' related task created and assigned to you by ' +
          practitionerName,
        practitionerName,
        orgCode,
        taskPayload.owner!.reference!.split('/')[1],
        'active',
        taskPayload.id
      );
      fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskPayload, flagPayload], provenance);
    } else {
      fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskPayload], provenance);
    }
    await fhirProxyClient.send('post', '', fhirBundle);
    setDimmerProcess(false);
    handleAddTaskToggle(false);
    clearForm();
    handleNewTask(taskPayload.id);
    setRenderURL(true);
  };

  const submitTaskBundle = async () => {
    // Guard against invalid intervals (e.g. 0/-1) that would hang the create loop.
    if (recurringInterval == null || recurringInterval < 1) {
      return;
    }

    const totalInterval = moment(formEndDate).diff(moment(formStartDate), 'days');
    let i = 0;

    setDimmerProcess(true);

    while (i < totalInterval) {
      // if task code is outreach, default reason to "enrollment"
      if (taskDetails.code.code === 'outreach') {
        taskDetails.reason = 'enrollment';
      }

      let taskPayload = TaskBuilder.build(
        taskDetails.code,
        null,
        taskDetails.description,
        'Patient/' + patientId.toString(),
        ['Organization/' + orgCode],
        null,
        taskDetails.reason,
        taskDetails.note,
        null,
        (taskDetails.startDate = moment(new Date()).format('YYYY-MM-DD')),
        (taskDetails.endDate = moment(formStartDate).add(i, 'days').format('YYYY-MM-DD')),
        taskDetails.ownerRef,
        practitionerName,
        taskDetails.restriction ? taskDetails.restriction.recipient : null,
        taskDetails.referralId,
        null,
        taskDetails.priority === 'asap' ? 'asap' : 'routine'
      );

      taskPayload.requester = {
        reference: 'Practitioner/' + practitionerId.toString(),
        display: practitionerName,
      };

      const orgRef = getOrganizationReference(orgCode);

      taskPayload = FHIRUtils.addUpdatedBy(taskPayload, practitionerName);

      const provenance = ProvenanceBuilder.build(
        `Task/${taskPayload.id}`,
        {
          reference: 'Practitioner/' + practitionerId,
          display: practitionerName,
        },
        patientId.toString(),
        'CREATE',
        `A ${taskPayload?.code?.coding?.[0]?.code} task has been created`,
        orgRef
      );

      let fhirBundle: Bundle;
      //do not create flag if a task is food delivery or consultation task
      if (!['consultation', 'food-delivery', 'other-goods-delivery'].includes(taskPayload?.code?.coding?.[0]?.code as string)) {
        const flagPayload = FlagBuilder.build(
          patientId.toString(),
          practitionerId,
          { code: 'admin', display: 'administrative' },
          { code: 'ALERT', display: 'Alert' },
          taskPayload.code!.coding![0]!.code!.toString().toUpperCase() +
            ' related task created and assigned to you by ' +
            practitionerName,
          practitionerName,
          orgCode,
          taskPayload.owner!.reference!.split('/')[1],
          'active',
          taskPayload.id
        );
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskPayload, flagPayload], provenance);
      } else {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskPayload], provenance);
      }

      await fhirProxyClient.send('post', '', fhirBundle);

      setDimmerProcess(false);
      handleAddTaskToggle(false);
      clearForm();
      handleNewTask(taskPayload.id);

      /*
       * Was a SUSPECTED DEFECT: `recurringInterval` is `useState<number | null>(null)`,
       * and with no interval entered `i += null` is `i + 0`, so the
       * `while (i < totalInterval)` above never advanced — the loop POSTed a new Task
       * to the FHIR proxy forever, until the tab was killed. Recorded as CP-1.
       *
       * Upstream's guard in `74e6ee29` retires it: validation rejects an interval below
       * 1, and this function returns early on `null` or `< 1`, so the state is
       * unreachable here. That early return also narrows the type, which is why the
       * `!` this line used to carry is gone — upstream has no assertion here either.
       */
      i += recurringInterval;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Fire-and-forget: the form's own dimmer and error state report progress, and a
    // submit handler cannot return a promise. `void` states what already happened.
    void validateTaskDetails();
  };

  return {
    clearForm,
    dimmerProcess,
    flatPractitioners,
    formCode,
    formDate,
    formDescription,
    formEndDate,
    formOwner,
    formPriorirty,
    formStartDate,
    formVendor,
    handleChange,
    handleSubmit,
    recurring,
    recurringInterval,
    referralId,
    setFormEndDate,
    setFormStartDate,
    setRecurring,
    setRecurringInterval,
    validationErrors,
    vendorList,
  };
};
